import useI18n from '@/shared/hooks/usei18n'
import { CaretDownIcon, CaretUpIcon, DataBlock } from '@/common'

type Props = {
  totalCertificates: number
  totalLiability: number
  totalCertificatesHigh?: number
  cscfPercentageFromModifiers?: number
  totalLiabilityLow?: number
  totalLiabilityHigh?: number
}

export default function CbamReportScenariosTotalsRow({
  totalCertificates,
  totalLiability,
  totalCertificatesHigh,
  cscfPercentageFromModifiers,
  totalLiabilityLow,
  totalLiabilityHigh
}: Props) {
  const { t, numberFormatter3Decimals, currencyFormatter2Decimals } = useI18n()

  const formatCertificates = (value: number) =>
    value > 0 ? `${numberFormatter3Decimals(value)} tCO₂` : '–'
  const formatLiability = (value: number) => (value > 0 ? currencyFormatter2Decimals(value) : '–')

  const scenarioHigherLabel = t(
    'cbamReporting:cbamReportDetails.estimator.summaryTotals.scenarioHigherImpact'
  )
  const scenarioLowerLabel = t(
    'cbamReporting:cbamReportDetails.estimator.summaryTotals.scenarioLowerImpact'
  )

  return (
    <div className="grid grid-cols-2 gap-6">
      <DataBlock
        label={t('cbamReporting:cbamReportDetails.estimator.summaryTotals.totalCertificates')}
        tooltip={t('cbamReporting:cbamReportDetails.estimator.summaryTotals.totalCertificatesTooltip')}
        data={formatCertificates(totalCertificates)}
        details={
          totalCertificatesHigh ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-0.5 text-sm font-medium text-gray-700">
                <CaretUpIcon className="size-6 shrink-0 text-yellow-400" aria-hidden />
                <span className="min-w-0 truncate text-yellow-500">
                  {scenarioHigherLabel}: {formatCertificates(totalCertificatesHigh)}
                  {cscfPercentageFromModifiers != null &&
                    ` (${t(
                      'cbamReporting:cbamReportDetails.estimator.summaryTotals.cscfFromModifiers',
                      {
                        value: cscfPercentageFromModifiers
                      }
                    )})`}
                </span>
              </div>
            </div>
          ) : undefined
        }
      />
      <DataBlock
        label={t('cbamReporting:cbamReportDetails.estimator.summaryTotals.totalLiability')}
        tooltip={t('cbamReporting:cbamReportDetails.estimator.summaryTotals.totalLiabilityTooltip')}
        data={formatLiability(totalLiability)}
        details={
          totalLiabilityHigh && totalLiabilityLow ? (
            <div className="flex gap-1">
              <div className="flex items-center gap-0.5 text-sm font-medium text-gray-700">
                <CaretUpIcon className="size-6 shrink-0 text-yellow-400" aria-hidden />
                <span className="min-w-0 truncate text-yellow-500">
                  {scenarioHigherLabel}: {formatLiability(totalLiabilityHigh)}
                </span>
              </div>
              <div className="flex items-center gap-0.5 text-sm font-medium text-gray-700">
                <CaretDownIcon className="size-6 shrink-0 text-blue-400" aria-hidden />
                <span className="min-w-0 truncate text-blue-500">
                  {scenarioLowerLabel}: {formatLiability(totalLiabilityLow)}
                </span>
              </div>
            </div>
          ) : undefined
        }
      />
    </div>
  )
}
